import { collectDescendantEmployeeIds } from "./reportsTeamScope";
import {
  formatMinutesAsHMM,
  parseDurationToDecimalHours,
  classifyTimesheetItemMinutes,
} from "../../utils/timesheetHelpers";

export const TIMESHEET_REPORT_KEY = "Timesheet summary report";
export const CLIENT_MANAGER_ROLE_ID = 2;
export const EMPLOYEE_ROLE_ID = 3;

export const formatTimesheetHours = (decimalHours) =>
  formatMinutesAsHMM(Math.round((decimalHours || 0) * 60));

/** Prefer duration-aware parsing (hours may exceed 24). */
export const parseTimesheetHours = parseDurationToDecimalHours;

function pickReportHours(...candidates) {
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const parsed = parseDurationToDecimalHours(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getLineItemsForTimesheet(ts, detailsById = {}) {
  const detailPayload = detailsById[ts.timesheet_id];
  if (detailPayload) {
    const entry = Array.isArray(detailPayload) ? detailPayload[0] : detailPayload;
    return entry?.itemDetails || [];
  }
  return Array.isArray(ts?.itemDetails) ? ts.itemDetails : [];
}

export const aggregateTimesheetDetails = (
  timesheets,
  detailsById,
  employees = [],
  activityList = [],
) => {
  const employeeNameById = employees.reduce((acc, emp) => {
    acc[emp.employee_id] = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    return acc;
  }, {});

  const grouped = {};

  timesheets.forEach((ts) => {
    const empId = ts.employee_id;
    if (!empId) return;

    if (!grouped[empId]) {
      grouped[empId] = {
        employee_name:
          ts.employee_name || employeeNameById[empId] || `Employee ${empId}`,
        billable: 0,
        nonBillable: 0,
        idle: 0,
        total: 0,
      };
    }

    const items = getLineItemsForTimesheet(ts, detailsById);

    if (items.length > 0) {
      items.forEach((item) => {
        const { billable, nonBillable, idle } = classifyTimesheetItemMinutes(
          item,
          activityList,
        );
        grouped[empId].billable += billable / 60;
        grouped[empId].nonBillable += nonBillable / 60;
        grouped[empId].idle += idle / 60;
        grouped[empId].total += (billable + nonBillable + idle) / 60;
      });
    } else {
      const total = parseTimesheetHours(ts.total_hours || ts.no_of_hours);
      grouped[empId].total += total;
    }
  });

  return Object.entries(grouped).map(([employee_id, row]) => ({
    employee_id,
    employee_name: row.employee_name,
    billable_hours: formatTimesheetHours(row.billable),
    non_billable_hours: formatTimesheetHours(row.nonBillable),
    idle_hours: formatTimesheetHours(row.idle),
    total_hours: formatTimesheetHours(row.total),
    _chart: {
      name: row.employee_name,
      billable: Number(row.billable.toFixed(2)),
      nonBillable: Number(row.nonBillable.toFixed(2)),
      idle: Number(row.idle.toFixed(2)),
      total: Number(row.total.toFixed(2)),
    },
  }));
};

export const mapApiTimesheetReportRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    // Prefer *_decimal from API; fall back to H:MM duration strings (hours may exceed 24).
    const billable = pickReportHours(
      row.billable_hours_decimal,
      row.billable_hours,
    );
    const nonBillable = pickReportHours(
      row.non_billable_hours_decimal,
      row.non_billable_hours,
    );
    const idle = pickReportHours(
      row.idle_hours_decimal,
      row.idle_hours,
      row.idle_time,
    );
    const total = pickReportHours(
      row.total_hours_decimal,
      row.total_hours,
    );
    const resolvedTotal = total || billable + nonBillable + idle;
    return {
      employee_id: row.employee_id ?? row.employeeId ?? null,
      employee_name: row.employee_name || row.employee || "—",
      billable_hours: formatTimesheetHours(billable),
      non_billable_hours: formatTimesheetHours(nonBillable),
      idle_hours: formatTimesheetHours(idle),
      total_hours: formatTimesheetHours(resolvedTotal),
      _chart: {
        name: row.employee_name || row.employee || "—",
        billable: Number(billable.toFixed(2)),
        nonBillable: Number(nonBillable.toFixed(2)),
        idle: Number(idle.toFixed(2)),
        total: Number(resolvedTotal.toFixed(2)),
      },
    };
  });
};

export const filterTimesheetsByManager = (
  timesheets,
  managerId,
  activeEmployees,
) => {
  if (!managerId || managerId === "All") return timesheets;
  const teamIds = collectDescendantEmployeeIds(activeEmployees, managerId, {
    includeSelf: false,
  });
  return (timesheets || []).filter((ts) =>
    teamIds.has(String(ts.employee_id)),
  );
};

function employeeDisplayName(emp) {
  return `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
}

function buildEmployeeNameIndex(activeEmployees = []) {
  const byName = new Map();
  activeEmployees.forEach((emp) => {
    const name = employeeDisplayName(emp).toLowerCase();
    if (name) byName.set(name, String(emp.employee_id));
  });
  return byName;
}

function resolveEmployeeIdFromRow(row, nameIndex) {
  if (row?.employee_id != null && row.employee_id !== "") {
    return String(row.employee_id);
  }
  const name = String(row?.employee_name || row?.employee || "")
    .trim()
    .toLowerCase();
  return name ? nameIndex.get(name) || null : null;
}

export function sumTimesheetHourRows(rows = []) {
  let billable = 0;
  let nonBillable = 0;
  let idle = 0;
  let total = 0;

  rows.forEach((row) => {
    billable += parseTimesheetHours(row?.billable_hours);
    nonBillable += parseTimesheetHours(row?.non_billable_hours);
    idle += parseTimesheetHours(row?.idle_hours);
    total += parseTimesheetHours(row?.total_hours);
  });

  const computedTotal = total || billable + nonBillable + idle;

  return {
    billable_hours: formatTimesheetHours(billable),
    non_billable_hours: formatTimesheetHours(nonBillable),
    idle_hours: formatTimesheetHours(idle),
    total_hours: formatTimesheetHours(computedTotal),
  };
}

function buildEmptyTimesheetHourRow(employeeName, employeeId = null) {
  return {
    employee_id: employeeId,
    employee_name: employeeName,
    billable_hours: "0:00",
    non_billable_hours: "0:00",
    idle_hours: "0:00",
    total_hours: "0:00",
  };
}

/**
 * Groups flat timesheet rows under client managers (role_id === 2) for accordion UI.
 */
export function buildManagerAccordionTimesheetRows(
  flatRows = [],
  activeEmployees = [],
) {
  if (!Array.isArray(activeEmployees) || !activeEmployees.length) {
    return [];
  }

  const nameIndex = buildEmployeeNameIndex(activeEmployees);
  const rowByEmployeeId = new Map();

  (flatRows || []).forEach((row) => {
    const employeeId = resolveEmployeeIdFromRow(row, nameIndex);
    if (!employeeId) return;
    rowByEmployeeId.set(employeeId, { ...row, employee_id: employeeId });
  });

  const managers = activeEmployees
    .filter((emp) => Number(emp.role_id) === CLIENT_MANAGER_ROLE_ID)
    .sort((a, b) =>
      employeeDisplayName(a).localeCompare(employeeDisplayName(b)),
    );

  return managers.map((manager) => {
    const managerId = String(manager.employee_id);
    const teamIds = collectDescendantEmployeeIds(activeEmployees, managerId, {
      includeSelf: false,
    });

    const children = activeEmployees
      .filter(
        (emp) =>
          teamIds.has(String(emp.employee_id)) &&
          Number(emp.role_id) === EMPLOYEE_ROLE_ID,
      )
      .map((emp) => {
        const empId = String(emp.employee_id);
        const existing = rowByEmployeeId.get(empId);
        return (
          existing || buildEmptyTimesheetHourRow(employeeDisplayName(emp), empId)
        );
      })
      .sort((a, b) =>
        String(a.employee_name || "").localeCompare(String(b.employee_name || "")),
      );

    const managerOwn = rowByEmployeeId.get(managerId);
    const summarySource = [
      ...children,
      ...(managerOwn ? [managerOwn] : []),
    ];

    return {
      managerId,
      employee_name: employeeDisplayName(manager),
      direct_reports_count: children.length,
      ...sumTimesheetHourRows(summarySource),
      children,
    };
  });
}

export function filterManagerAccordionRows(
  rows = [],
  {
    isManagerView = false,
    loggedManagerId = "",
    selectedManager = "All",
    selectedEmployee = "All",
  } = {},
) {
  let next = rows;

  if (isManagerView && loggedManagerId) {
    next = next.filter(
      (row) => String(row.managerId) === String(loggedManagerId),
    );
  } else if (selectedManager !== "All") {
    next = next.filter(
      (row) => String(row.managerId) === String(selectedManager),
    );
  }

  if (selectedEmployee !== "All") {
    next = next
      .map((row) => ({
        ...row,
        children: (row.children || []).filter(
          (child) => String(child.employee_id) === String(selectedEmployee),
        ),
      }))
      .filter((row) => row.children.length > 0);
  }

  return next;
}
